import produce from "immer";
import { message } from "antd";
import { DEFAULT_TREE_TYPE } from "../configs/constants";
import { Scenario } from "../types";

const initialState: Scenario | null = null;

const scenario = (state = initialState, action): Scenario | null => {
  function getBlockgroup(draftState: Scenario, id) {
    for (let municipality of draftState.municipalities) {
      for (let blockgroup of municipality.blockgroups) {
        if (blockgroup.GEOID === id) return blockgroup;
      }
    }
  }
  function getParcel(draftState: Scenario, id) {
    for (let municipality of draftState.municipalities) {
      for (let blockgroup of municipality.blockgroups) {
        for (let parcel of blockgroup.parcels) {
          if (parcel.PARCEL_ID === id) return parcel;
        }
      }
    }
  }

  function getFeature(draftState: Scenario, id: string) {
    for (let municipality of draftState.municipalities) {
      for (let blockgroup of municipality.blockgroups) {
        if (Object.keys(blockgroup).includes("features")) {
          for (let feature of blockgroup.features) {
            if (feature.id === id) return feature;
          }
        }
      }
    }
  }
  function addUnique(list, toAdd, id) {
    // Defensive copy: react-redux apparently freezes objects
    // that are passed in actions
    let copy = { ...toAdd };
    const existing = list.find((item) => item[id] === toAdd[id]);
    if (existing) return existing;
    list.push(copy);
    return copy;
  }
  let nextState: Scenario;
  switch (action.type) {
    case "SCENARIO_ADD":
      return produce(state, () => action.payload.data);
    case "SCENARIO_GET":
      return produce(state, () => action.payload.data);
    case "SCENARIO_DELETE":
      return null;
    case "SCENARIO_RESET":
      return null;
    case "SCENARIO_TITLE_EDIT":
      nextState = produce(state, (draftState) => {
        draftState.name = action.data.value;
      });
      return nextState;
    case "SCENARIO_GEOMETRY_DELETE":
      //console.log(action);
      nextState = produce(state, (draftState) => {
        if (action.data.type === "municipality") {
          draftState.municipalities = draftState.municipalities.filter(
            (municipality) => municipality.NAME !== action.data.id
          );
        } else if (action.data.type === "blockgroup") {
          // get child parcels
          for (let municipality of draftState.municipalities) {
            municipality.blockgroups = municipality.blockgroups.filter(
              (blockgroup) => blockgroup.GEOID !== action.data.id
            );
          }
        }
        if (action.data.type === "parcel") {
          for (let municipality of draftState.municipalities) {
            for (let blockgroup of municipality.blockgroups) {
              blockgroup.parcels = blockgroup.parcels.filter(
                (parcel) => parcel.PARCEL_ID !== action.data.id
              );
            }
          }
        }
        if (action.data.type === "feature") {
          for (let municipality of draftState.municipalities) {
            for (let blockgroup of municipality.blockgroups) {
              blockgroup.features =
                blockgroup.features.filter(
                  (feature) => feature.properties.id !== action.data.id
                ) || [];
            }
          }
        }
      });
      return nextState;
    case "SCENARIO_BLOCKGROUP_ADD":
      nextState = produce(state, (draftState) => {
        if (!action.data.id) return;
        const municipality = addUnique(
          draftState.municipalities,
          action.data.municipality.properties,
          "NAME"
        );
        municipality.blockgroups = municipality.blockgroups || [];
        const blockgroup = addUnique(
          municipality.blockgroups,
          action.data.value,
          "GEOID"
        );
        blockgroup.parcels = blockgroup.parcels || [];
        message.success("Blockgroup added");
      });
      return nextState;
    case "SCENARIO_PARCEL_ADD":
      nextState = produce(state, (draftState) => {
        const municipality = addUnique(
          draftState.municipalities,
          action.data.municipality.properties,
          "NAME"
        );
        municipality.blockgroups = municipality.blockgroups || [];
        const blockgroup = addUnique(
          municipality.blockgroups,
          {
            ...action.data.blockgroup,
            treeType: DEFAULT_TREE_TYPE,
          },
          "GEOID"
        );
        blockgroup.parcels = blockgroup.parcels || [];
        addUnique(
          blockgroup.parcels,
          {
            ...action.data.value,
            treeType: DEFAULT_TREE_TYPE,
          },
          "PARCEL_ID"
        );
        message.success("Parcel added");
      });
      return nextState;
    case "SCENARIO_ADD_FEATURE":
      nextState = produce(state, (draftState) => {
        const municipality = addUnique(
          draftState.municipalities,
          action.data.municipality.properties,
          "NAME"
        );
        municipality.blockgroups = municipality.blockgroups || [];
        const blockgroup = addUnique(
          municipality.blockgroups,
          action.data.blockgroup,
          "GEOID"
        );
        blockgroup.features = (blockgroup.features || []).concat(
          action.data.value
        );
        blockgroup.parcels = blockgroup.parcels || [];
        message.success("Custom area added");
      });
      return nextState;
    case "SCENARIO_CANOPY_GOAL_EDIT":
      nextState = produce(state, (draftState) => {
        const blockgroup = getBlockgroup(draftState, action.data.id);
        blockgroup.acresAdded = action.data.value;
      });
      return nextState;
    case "SCENARIO_SAVE":
      //console.log("scenario saved...");
      return state;

    case "PARCEL_SPECIES_PROPERTY_EDIT":
      nextState = produce(state, (draftState) => {
        const parcel = getParcel(draftState, action.data.id);
        parcel.treeType = action.data.value;
      });
      return nextState;

    case "FEATURE_SPECIES_PROPERTY_EDIT":
      nextState = produce(state, (draftState) => {
        const feature = getFeature(draftState, action.data.id);
        feature.properties.treeType = action.data.value;
      });
      return nextState;

    case "BLOCKGROUP_SPECIES_PROPERTY_EDIT":
      nextState = produce(state, (draftState) => {
        const blockgroup = getBlockgroup(draftState, action.data.id);
        blockgroup.treeType = action.data.value;
      });
      return nextState;

    case "PARCEL_ACRES_ADDED_EDIT":
      nextState = produce(state, (draftState) => {
        const parcel = getParcel(draftState, action.data.id);
        parcel.acresAdded = action.data.value;
      });
      return nextState;

    case "FEATURE_ACRES_ADDED_EDIT":
      nextState = produce(state, (draftState) => {
        const feature = getFeature(draftState, action.data.id);
        feature.properties.treesAdded = action.data.value;
      });
      return nextState;

    default:
      return state;
  }
};

export default scenario;
